'use client';
import type { ChangeEvent, InputHTMLAttributes, ReactNode, Ref } from 'react';

import { Icon, type IconType } from '@/app/components/icons';
import { cn } from '@/app/utils/cn';
import { safeCall } from '@/app/utils/safeCall';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'checked'> {
  ref?: Ref<HTMLLabelElement> | null;
  children?: ReactNode;
  iconBefore?: IconType;
  iconAfter?: ReactNode;
  checked?: boolean;
  className?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

export const Tag = ({
  ref,
  iconBefore,
  iconAfter,
  children,
  checked = false,
  onChange,
  className,
  id,
  ...props
}: CheckboxProps) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    safeCall(onChange, event);
  };

  return (
    <label
      ref={ref}
      htmlFor={id}
      onClick={(event) => {
        event.stopPropagation();
      }}
      className={cn(
        'group rounded-2xs relative inline-flex h-8 w-full max-w-fit cursor-pointer items-center justify-center gap-x-2 px-2 py-1 whitespace-pre',
        'transition-form text-body-1 border',
        {
          'border-gray-800 bg-white text-gray-900 hover:border-transparent hover:bg-gray-50 hover:text-gray-900':
            !checked,
          'border-transparent bg-gray-800 text-white': checked,
        },
        className
      )}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        {...props}
      />
      {!!iconBefore && <Icon icon={iconBefore} className="size-5" />}
      {!!children && (
        <div className="relative">
          <strong
            className={cn({
              'opacity-0': !checked,
            })}
          >
            {children}
          </strong>
          <span
            className={cn('absolute inset-0', {
              'opacity-0': checked,
            })}
          >
            {children}
          </span>
        </div>
      )}
      {!!iconAfter && iconAfter}
    </label>
  );
};
